import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    Globe,
    BarChart3,
    Activity,
    MousePointerClick,
    Cloud,
    MonitorSmartphone,
    ArrowRight,
    CheckCircle2,
} from 'lucide-react';

const features = [
    {
        icon: Globe,
        title: 'No Download Needed',
        description:
            `Get started instantly without installing any software. The full ${siteConfig.platform.name} experience runs directly in your web browser -- Chrome, Firefox, Safari, or Edge.`,
    },
    {
        icon: BarChart3,
        title: 'Full Charting Suite',
        description:
            'Professional charting with 70+ technical indicators, multiple timeframes, and advanced drawing tools -- all in your browser.',
    },
    {
        icon: Activity,
        title: 'Real-Time Data',
        description:
            'Live streaming quotes, real-time market depth, and instant order execution with no delays or data lag.',
    },
    {
        icon: MousePointerClick,
        title: 'One-Click Trading',
        description:
            'Execute trades instantly with one-click order placement. Manage positions, set stop-losses, and take-profits with minimal latency.',
    },
    {
        icon: Cloud,
        title: 'Cloud-Based',
        description:
            'Your settings, watchlists, and layouts are stored in the cloud. Access your personalized workspace from any device, anywhere.',
    },
    {
        icon: MonitorSmartphone,
        title: 'Responsive Design',
        description:
            'Optimized for any screen size -- from large desktop monitors to tablets. The interface adapts to deliver the best experience.',
    },
];

const benefits = [
    'Access from any computer or device with a browser',
    'Synchronized with desktop and mobile platforms',
    'Full order management and trade execution',
    'Real-time alerts and notifications',
    'Secure, encrypted connection',
    'No installation or updates needed',
];

export default function Web() {
    return (
        <AppLayout title={`${siteConfig.platform.shortName} Web - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="bg-background pt-32 lg:pt-40 pb-20">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Web Platform</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            {siteConfig.platform.name} <span className="text-gradient">Web</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
                            Trade from Any Browser
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.3}>
                        <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                            <Button size="lg">
                                Get Started
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </a>
                    </FadeIn>
                </div>
            </Section>

            {/* Features */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Web Platform"
                        highlight="Features"
                        description="Everything you need to trade effectively, accessible from any browser."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div key={feature.title} variants={staggerItem}>
                                    <Card className="h-full rounded-xl border border-border bg-card/80 backdrop-blur-sm transition-colors hover:border-primary/20">
                                        <CardContent className="p-6 lg:p-8">
                                            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                <Icon className="h-5 w-5 text-primary" />
                                            </div>
                                            <h3 className="mb-2 text-lg font-semibold text-foreground">
                                                {feature.title}
                                            </h3>
                                            <p className="text-sm leading-relaxed text-muted-foreground">
                                                {feature.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Benefits */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Why Choose"
                        highlight={`${siteConfig.platform.shortName} Web`}
                        description="The convenience of browser-based trading without compromising on features."
                    />
                </FadeIn>
                <FadeIn delay={0.1}>
                    <div className="max-w-2xl mx-auto">
                        <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                            <CardContent className="p-8">
                                <StaggerChildren>
                                    <ul className="space-y-4">
                                        {benefits.map((benefit) => (
                                            <motion.li
                                                key={benefit}
                                                variants={staggerItem}
                                                className="flex items-center gap-3 text-muted-foreground"
                                            >
                                                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 border border-primary/10 shrink-0">
                                                    <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                                                </div>
                                                <span>{benefit}</span>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </StaggerChildren>
                            </CardContent>
                        </Card>
                    </div>
                </FadeIn>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Start Trading in Your <span className="text-gradient">Browser</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8">
                            No downloads, no installations. Get funded and start trading on {siteConfig.platform.shortName} Web today.
                        </p>
                        <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                            <Button size="lg">
                                Get Started
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </a>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
